import React, { useMemo } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const STOCK_COLORS = ["#2563EB", "#16A34A", "#D97706", "#7C3AED", "#64748B"];
const VALUE_COLORS = ["#5D38DB", "#0D9488", "#D97706", "#7C3AED", "#64748B"];

const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {isCurrency ? formatCurrency(item.value) : formatLitres(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const OwnerStockInsightsChart = ({
  stockData = [],
  stockSeries = [],
  valueData = [],
  valueSeries = [],
  summary = {},
  byCategory = [],
  fuelByProduct = [],
  tanks = [],
  topNfProducts = [],
  loading = false,
}) => {
  const stockAreaSeries = useMemo(
    () => stockSeries.filter((item) => item.type === "fuel_stock"),
    [stockSeries]
  );
  const stockTotalSeries = useMemo(
    () => stockSeries.find((item) => item.key === "Total"),
    [stockSeries]
  );
  const valueAreaSeries = useMemo(
    () => valueSeries.filter((item) => item.type === "fuel_value"),
    [valueSeries]
  );
  const valueTotalSeries = useMemo(
    () => valueSeries.find((item) => item.key === "Total"),
    [valueSeries]
  );

  const mixData = useMemo(
    () =>
      byCategory.map((item, index) => ({
        name: item.category,
        value: item.value,
        pct: item.pct,
        fill: STOCK_COLORS[index % STOCK_COLORS.length],
      })),
    [byCategory]
  );

  const tankFillData = useMemo(
    () =>
      tanks
        .filter((tank) => tank.fill_pct > 0 || tank.litres > 0)
        .map((tank) => ({
          name: tank.name,
          fill_pct: tank.fill_pct,
          litres: tank.litres,
          fuel: tank.fuel,
        })),
    [tanks]
  );

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!summary?.total_stock_value && !stockData.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-database owner-chart-empty__icon" aria-hidden="true" />
        <p>No stock data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Inventory value</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.total_stock_value)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Fuel stock</span>
            <strong className="owner-sales-kpi__value">
              {formatLitres(summary?.fuel_stock_litres)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.fuel_stock_value)}
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--teal">
            <span className="owner-sales-kpi__label">Non-fuel stock</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.non_fuel_stock_value)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {summary?.non_fuel_stock_qty || 0} units
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Avg tank fill</span>
            <strong className="owner-sales-kpi__value">
              {formatPercent(summary?.avg_tank_fill_pct)}
            </strong>
            {summary?.cylinder_stock_value > 0 ? (
              <small className="owner-sales-kpi__hint">
                Cylinder: {formatCurrency(summary.cylinder_stock_value)}
              </small>
            ) : null}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Fuel stock trend (litres)</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={stockData} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<ChartTooltip isCurrency={false} />} />
                <Legend />

                {stockAreaSeries.map((item, index) => (
                  <Area
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label}
                    stackId="stock"
                    stroke={STOCK_COLORS[index % STOCK_COLORS.length]}
                    fill={STOCK_COLORS[index % STOCK_COLORS.length]}
                    fillOpacity={0.35}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}

                {stockTotalSeries ? (
                  <Line
                    type="monotone"
                    dataKey={stockTotalSeries.key}
                    name={stockTotalSeries.label}
                    stroke="#5D38DB"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "#5D38DB", strokeWidth: 0 }}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Fuel stock value trend (₹)</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={valueData} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<ChartTooltip isCurrency />} />
                <Legend />

                {valueAreaSeries.map((item, index) => (
                  <Area
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label}
                    stackId="value"
                    stroke={VALUE_COLORS[index % VALUE_COLORS.length]}
                    fill={VALUE_COLORS[index % VALUE_COLORS.length]}
                    fillOpacity={0.3}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}

                {valueTotalSeries ? (
                  <Line
                    type="monotone"
                    dataKey={valueTotalSeries.key}
                    name={valueTotalSeries.label}
                    stroke="#5D38DB"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "#5D38DB", strokeWidth: 0 }}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-4">
          <div className="owner-chart-panel owner-chart-panel--compact h-100">
            <p className="owner-chart-panel__subtitle mb-2">Inventory value mix</p>
            {mixData.length ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={mixData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {mixData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        formatCurrency(value),
                        `${name} (${formatPercent(props?.payload?.pct)})`,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="owner-chart-legend mt-2">
                  {mixData.map((item) => (
                    <div className="owner-chart-legend__item" key={item.name}>
                      <span
                        className="owner-chart-legend__swatch"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span>
                        {item.name} ({formatPercent(item.pct)})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="owner-chart-empty owner-chart-empty--compact">
                <p>No inventory value breakdown.</p>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          <div className="owner-chart-panel h-100">
            <p className="owner-chart-panel__subtitle mb-2">Tank fill levels (current)</p>
            {tankFillData.length ? (
              <ResponsiveContainer width="100%" height={Math.max(220, tankFillData.length * 42)}>
                <BarChart
                  data={tankFillData}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fill: "#334155", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value, _name, props) => [
                      `${formatPercent(value)} · ${formatLitres(props?.payload?.litres)}`,
                      props?.payload?.fuel || "Tank",
                    ]}
                  />
                  <Bar dataKey="fill_pct" name="Fill %" radius={[0, 6, 6, 0]} barSize={20}>
                    {tankFillData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={
                          entry.fill_pct >= 75
                            ? "#16A34A"
                            : entry.fill_pct >= 40
                            ? "#D97706"
                            : "#DC2626"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="owner-chart-empty owner-chart-empty--compact">
                <p>No tank data available.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {fuelByProduct.length > 0 ? (
        <div className="owner-chart-panel mb-4">
          <p className="owner-chart-panel__subtitle mb-2">Fuel stock by product (current)</p>
          <div className="table-responsive">
            <table className="table table-sm owner-employee-table mb-0">
              <thead>
                <tr>
                  <th>Fuel</th>
                  <th className="text-end">Litres</th>
                  <th className="text-end">WAC</th>
                  <th className="text-end">Value</th>
                </tr>
              </thead>
              <tbody>
                {fuelByProduct.map((row) => (
                  <tr key={row.fuel}>
                    <td>{row.fuel}</td>
                    <td className="text-end">{formatLitres(row.litres)}</td>
                    <td className="text-end">{formatCurrency(row.wac)}/L</td>
                    <td className="text-end">{formatCurrency(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {topNfProducts.length > 0 ? (
        <div className="owner-chart-panel">
          <p className="owner-chart-panel__subtitle mb-2">Top non-fuel stock by value</p>
          <div className="table-responsive">
            <table className="table table-sm owner-employee-table mb-0">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Buy price</th>
                  <th className="text-end">Value</th>
                </tr>
              </thead>
              <tbody>
                {topNfProducts.map((row) => (
                  <tr key={row.product_id}>
                    <td>{row.name}</td>
                    <td className="text-end">{row.quantity}</td>
                    <td className="text-end">{formatCurrency(row.purchase_price)}</td>
                    <td className="text-end">{formatCurrency(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OwnerStockInsightsChart;
